import { MobileBehaviourService } from './services/mobile.behaviour.service';
import { ChangeDetectionStrategy, Component, Injector, OnInit } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { DEFAULT_INTERRUPTSOURCES, Idle } from "@ng-idle/core";
import { OAuthService } from "@royalty/oauth2";
import { AuthenticationService } from "@royalty/service/auth";
import { DeviceDetectorService } from "ngx-device-detector";
import { UserSettingService } from "@royalty/service";
import { SwUpdate } from "@angular/service-worker";
import { from } from "rxjs";
import { filter, finalize, map, mergeMap } from "rxjs/operators";
import { posEventName, PosSharedService } from "./components/pos/shared/pos.shared.services";
import { SQLiteStorageService } from "./core/storage/sqlite-storage.service";
import { CoreToolbox } from "./core/utils/core-toolbox";
import { LocalStorageKey, RouteData } from "./model";
import { AppUtils } from './core/utils/app-utils';
import { StaticInjectorService } from './core/utils/static-injector';

@Component({
	selector: "app-root",
	templateUrl: "./app.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrls: ["./app.component.scss"]
})
export class AppComponent implements OnInit {
	public routeData: RouteData;
	public appClass: string;
	public breadcrumbs: string;

	constructor(
		private swUpdate: SwUpdate,
		private route: ActivatedRoute,
		private router: Router,
		private authenticationService: AuthenticationService,
		private oAuthService: OAuthService,
		private posShareService: PosSharedService,
		private idle: Idle,
		private deviceService: DeviceDetectorService,
		private userSettingService: UserSettingService,
		private injector: Injector,
		private mobileBehaviour: MobileBehaviourService
	) {
	}

	ngOnInit() {
		this.initializeCoreService();

		// Inititalize Storage
		from(CoreToolbox.storageService.initialize()).pipe(
			finalize(() => {
				this.intializeAppHandler();

				// Enable mobile handle with mobile mode. Avoid confflict with web behaviour
				if (AppUtils.isMobileApp) {
					this.mobileBehaviour.enableMobileBehaviour();
				}
			})
		).subscribe(x => {
			// After init native lib
		});

		if (this.swUpdate.isEnabled) {
			console.log("SW is enabled");
			this.swUpdate.checkForUpdate().then((version) => {
				console.log(
					`Updated new version: ${version} ${new Date().getTime()}`
				);
			})
				.catch((error) => {
					console.error(`Failed in updating app version ${error}`);
				});
			this.swUpdate.available.subscribe(() => {
				document.location.reload();
			});
		}
	}

	private intializeAppHandler() {
		this.setDeviceTypeUsing();

		this.userSettingService.useDefaultLanguage();

		this.oAuthService.setStorage(CoreToolbox.storageService);
		if (this.oAuthService.hasValidAccessToken()) {
			this.authenticationService.init();

			// Strategy for refresh token through a scheduler.
			this.authenticationService.startupTokenRefresh();
		}

		this.subscribeNavigationEnd();

		(CoreToolbox.storageService as SQLiteStorageService).onChangeStorageData.subscribe(obj => {
			this.storageEventListener(obj as StorageEvent);
		});

		this.listenUnAuthorize();

		// Try to workarround for fix issue idle
		const timeRefreshPage = 100;
		// Check idle
		// sets an idle timeout of 5 seconds
		this.idle.setIdle(5);
		// sets a timeout period of 5 seconds. after 10 seconds of inactivity, the user will be considered timed out.
		this.idle.setTimeout(timeRefreshPage);
		// sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
		this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

		this.idle.onTimeout.subscribe(() => {
			if (
				CoreToolbox.storageService.getItem(LocalStorageKey.POS_VIEW) ===
				LocalStorageKey.POS_VIEW
			) {
				location.reload();
			}
			this.resetIdle();
		});

		this.resetIdle();

		debugger;
	}

	private setDeviceTypeUsing() {
		let device = "";
		if (this.deviceService.isDesktop()) {
			device = "DESKTOP";
		} else if (this.deviceService.isTablet()) {
			device = "TABLET";
		} else if (this.deviceService.isMobile()) {
			device = "MOBILE";
		}
		CoreToolbox.storageService.setItem(LocalStorageKey.X_DEVICE_TYPE, device);
	}

	private resetIdle() {
		this.idle.watch();
	}

	private listenUnAuthorize(): void {
		this.posShareService
			.getObservable(posEventName.unAuthorize)
			.subscribe(isUnAuthorize => {
				if (isUnAuthorize) {
					this.authenticationService.signout();
				}
			});
	}

	private storageEventListener(event: StorageEvent) {
		if (event.storageArea == localStorage) {
			if (event.key === LocalStorageKey.access_token) {
				if (event.oldValue !== null) {
					if (event.newValue === null) {
						window.location.reload();
					}
				}
			}
		}
	}

	private subscribeNavigationEnd() {
		this.router.events
			.pipe(
				filter(e => e instanceof NavigationEnd),
				map(() => this.route),
				map(() => {
					let route = this.route.firstChild;
					let child = route;
					while (child) {
						if (child.firstChild) {
							child = child.firstChild;
							route = child;
						} else {
							child = null;
						}
					}
					return route;
				}),
				mergeMap(route => route.data)
			)
			.subscribe(e => {
				this.routeData = e as RouteData;
				this.appClass = this.routeData.hideHeader
					? "no-header"
					: "has-header";
				this.appClass += this.routeData.hideFooter
					? " no-footer"
					: " has-footer";
				this.breadcrumbs = this.routeData.name;
			});
	}

	/**
	 * Init all core service in here.
	 */
	private initializeCoreService() {
		// Init injector from app root. Make sure injector is inited firstly
		StaticInjectorService.injector = this.injector;
		// Init initialize for all core service
		CoreToolbox.appUpdateService.initialize({ action: "init", from: "pos" });
		CoreToolbox.networkService.intinialize();
		CoreToolbox.mobileService.intinialize();
	}
}
